import { z } from 'zod';
// -----------------------------------------------------------------------
// Stage
// -----------------------------------------------------------------------
// 'new' is the default for untouched landlords (rendered grey/blank in UI).
// Agents move records through the other 10 outcomes after contact.
export const LandlordStageSchema = z.enum([
    'new',
    'sale',
    'sold',
    'rent',
    'rented',
    'potential',
    'unavailable',
    'no_sale',
    'no_answer',
    'wrong_number',
    'agent',
]);
// -----------------------------------------------------------------------
// Search query
// -----------------------------------------------------------------------
export const LandlordSearchModeSchema = z.enum(['smart', 'name', 'phone', 'building_unit']);
export const LandlordSearchQuerySchema = z.object({
    q: z.string().optional(),
    mode: LandlordSearchModeSchema.default('smart'),
    area_id: z.coerce.number().int().positive().optional(),
    building: z.string().optional(),
    assigned_to: z.coerce.number().int().positive().optional(),
    stage: LandlordStageSchema.optional(),
    has_contact: z
        .string()
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    needs_review: z
        .string()
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    is_starred: z
        .string()
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    // Corporate-entity filter:
    //   'false' (default) — hide banks/LLCs/developers from default views
    //   'true'            — show only corporate entities
    //   'all'             — no filter (show both)
    is_corporate: z.enum(['true', 'false', 'all']).default('false'),
    tag_id: z.coerce.number().int().positive().optional(),
    updated_since: z.string().optional(),
    // 'name'       — alphabetic by display_name
    // 'recent'     — by latest activity (MAX(activity_events.created_at) per group)
    // 'units_desc' — by total_units descending (largest portfolios first)
    // 'imported'   — by landlords.created_at descending (newest DLD imports first)
    sort: z.enum(['name', 'recent', 'units_desc', 'imported']).default('name'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    // Include archived leads in results (default: false — archived leads are hidden).
    include_archived: z
        .string()
        .optional()
        .transform((v) => v === 'true'),
});
// -----------------------------------------------------------------------
// Phone record
// -----------------------------------------------------------------------
export const LandlordPhoneSchema = z.object({
    id: z.number().int(),
    phone: z.string(), // may be masked
    isMobile: z.boolean(),
    isPrimary: z.boolean(),
    isSwitchboard: z.boolean(),
});
// -----------------------------------------------------------------------
// Email record
// -----------------------------------------------------------------------
export const LandlordEmailSchema = z.object({
    id: z.number().int(),
    email: z.string(),
    isPrimary: z.boolean(),
});
// -----------------------------------------------------------------------
// Unit record
// -----------------------------------------------------------------------
export const UnitSchema = z.object({
    id: z.number().int(),
    unitNo: z.string().nullable(),
    beds: z.string().nullable(),
    view: z.string().nullable(),
    areaId: z.number().int().nullable(),
});
// -----------------------------------------------------------------------
// Tag record
// -----------------------------------------------------------------------
export const TagSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});
// -----------------------------------------------------------------------
// Landlord list item (search results)
// -----------------------------------------------------------------------
export const LandlordListItemSchema = z.object({
    id: z.number().int(), // representative MIN(id) — used for portfolio link
    name: z.string(),
    totalUnits: z.number().int(), // sum across all rows for this name
    buildingCount: z.number().int(), // COUNT DISTINCT buildings
    areaCount: z.number().int(), // COUNT DISTINCT areas
    stage: LandlordStageSchema, // most-actionable stage across this person's rows
    hasContact: z.boolean(), // BOOL_OR across rows
    needsReview: z.boolean(), // BOOL_OR across rows
    isStarred: z.boolean(), // BOOL_OR across rows
    phone: LandlordPhoneSchema.nullable(), // single primary phone (deduped)
    tags: z.array(TagSchema), // deduplicated union
    updatedAt: z.string().datetime(), // MAX across rows
});
// -----------------------------------------------------------------------
// Landlord detail (full)
// -----------------------------------------------------------------------
export const LandlordDetailSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    areaId: z.number().int().nullable(),
    areaName: z.string().nullable(),
    building: z.string().nullable(),
    stage: LandlordStageSchema,
    hasContact: z.boolean(),
    needsReview: z.boolean(),
    isStarred: z.boolean(),
    unitCount: z.number().int(),
    phones: z.array(LandlordPhoneSchema),
    tags: z.array(TagSchema),
    assignedAgentId: z.number().int().nullable(),
    assignedAgentName: z.string().nullable(),
    notes: z.string().nullable(),
    emails: z.array(LandlordEmailSchema),
    units: z.array(UnitSchema),
    updatedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
});
// -----------------------------------------------------------------------
// Patch landlord body
// -----------------------------------------------------------------------
export const PatchLandlordSchema = z
    .object({
    name: z.string().min(1).optional(),
    building: z.string().optional(),
    stage: LandlordStageSchema.optional(),
    notes: z.string().optional(),
    needsReview: z.boolean().optional(),
    isStarred: z.boolean().optional(),
})
    .refine((d) => Object.keys(d).length > 0, { message: 'At least one field required' });
// -----------------------------------------------------------------------
// Unit list (per-unit row in the landlords list page) — 2026-05-02
// -----------------------------------------------------------------------
// One row per `units` record, joined to its parent landlord for contact +
// stage/star/needs_review. Replaces grouped `LandlordListItem` for the
// list page; the drawer/portfolio still use grouped `LandlordListItem`.
export const UnitListItemSchema = z.object({
    unitId: z.number().int(),
    landlordId: z.number().int(),
    ownerName: z.string(),
    building: z.string().nullable(),
    unitNo: z.string().nullable(),
    view: z.string().nullable(),
    beds: z.string().nullable(),
    areaId: z.number().int().nullable(),
    areaName: z.string().nullable(),
    primaryMobile: LandlordPhoneSchema.nullable(),
    primaryEmail: z.string().nullable(),
    importedAt: z.string().datetime(),
    // Aggregated parent-landlord state (PR1: still on landlords;
    // moves to units in Phase B).
    stage: LandlordStageSchema,
    hasContact: z.boolean(),
    needsReview: z.boolean(),
    isStarred: z.boolean(),
    isCorporate: z.boolean(),
    tags: z.array(TagSchema),
    assignedAgentId: z.number().int().nullable(),
    assignedAgentName: z.string().nullable(),
    lastActivityAt: z.string().datetime().nullable(),
    // Sherzod 2026-05-04: per-row context for the per-unit list view.
    // landlordTotalUnits = total units owned by the parent landlord,
    // latestComment = body of the most recent unit_comments row on
    // THIS unit (null if no comments).
    landlordTotalUnits: z.number().int(),
    latestComment: z.string().nullable(),
});
// Query for /api/units. Mostly the same filters as LandlordSearchQuery
// but a different sort vocabulary (per-unit columns).
export const UnitSearchQuerySchema = z.object({
    q: z.string().optional(),
    mode: LandlordSearchModeSchema.default('smart'),
    area_id: z.coerce.number().int().positive().optional(),
    building: z.string().optional(),
    assigned_to: z.coerce.number().int().positive().optional(),
    stage: LandlordStageSchema.optional(),
    has_contact: z.string().optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    needs_review: z.string().optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    is_starred: z.string().optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
    is_corporate: z.enum(['true', 'false', 'all']).default('false'),
    tag_id: z.coerce.number().int().positive().optional(),
    // Per-unit sort options. 'total_units' partitions by UPPER(TRIM(name))
    // and counts units per name group within the active filter scope —
    // drives the Top Investors button + the TOTAL UNITS column header
    // (Sherzod 2026-05-06).
    sort: z.enum([
        'name', 'building', 'unit', 'view', 'beds',
        'stage', 'imported', 'recent', 'total_units',
    ]).default('imported'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    // Include archived leads in results (default: false — archived leads are hidden).
    include_archived: z
        .string()
        .optional()
        .transform((v) => v === 'true'),
});
// -----------------------------------------------------------------------
// PATCH /api/units/:id body — admin-only inline edit of view + beds
// -----------------------------------------------------------------------
export const PatchUnitSchema = z
    .object({
    view: z.string().nullable().optional(),
    beds: z.string().nullable().optional(),
    unitNo: z.string().nullable().optional(),
})
    .refine((d) => Object.keys(d).length > 0, { message: 'At least one field required' });
// -----------------------------------------------------------------------
// PATCH /api/units/:id extended body — Slice C (migration 013)
// Adds notes, assignee_id, followup_date on top of the existing fields.
// Role: agent or admin (the route enforces this; agents can update their own units).
// -----------------------------------------------------------------------
export const PatchUnitExtendedSchema = z
    .object({
    view: z.string().nullable().optional(),
    beds: z.string().nullable().optional(),
    unitNo: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    assigneeId: z.number().int().positive().nullable().optional(),
    followupDate: z.string().nullable().optional(), // ISO date string YYYY-MM-DD or null
    stage: LandlordStageSchema.optional(),
})
    .refine((d) => Object.keys(d).length > 0, { message: 'At least one field required' });
// -----------------------------------------------------------------------
// POST /api/units/:id/comments body
// -----------------------------------------------------------------------
export const UnitCommentSchema = z.object({
    body: z.string().min(1, 'Comment body cannot be empty').max(10000),
});
// -----------------------------------------------------------------------
// POST /api/units/:id/tags body
// -----------------------------------------------------------------------
export const UnitTagSchema = z.object({
    tag_id: z.number().int().positive(),
});
// -----------------------------------------------------------------------
// POST /api/units/:id/phones body
// -----------------------------------------------------------------------
export const UnitPhoneSchema = z.object({
    phone: z.string().min(5, 'Phone number too short').max(20),
    is_primary: z.boolean().optional().default(false),
});
// -----------------------------------------------------------------------
// POST /api/landlords/:id/archive body
// -----------------------------------------------------------------------
export const ArchiveLandlordSchema = z.object({
    reason: z.string().min(1, 'Reason is required').max(255, 'Reason must be 255 characters or less'),
});
// -----------------------------------------------------------------------
// GET /api/landlords/junk-review query
// -----------------------------------------------------------------------
export const JunkReviewQuerySchema = z.object({
    status: z.enum(['archived', 'active', 'all']).default('archived'),
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    flag: z
        .enum([
        'name_is_building',
        'placeholder_name',
        'landline_only',
        'no_phone_no_email',
        'no_mobile_no_email',
    ])
        .optional(),
});
//# sourceMappingURL=landlord.js.map